// src/app/services/gtm.service.ts
import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import {
    eventStates,
    GTM_EVENTS,
    PAGE_NAMES,
    PLATFORM_ENUM,
    RealTimeEventStatesEnum,
    TimelineEventStatesEnum,
} from "./gtm-events";
import { GTM_EVENT_CONFIG } from "./gtm-events-config";
import { UserPropertiesService } from "../user-services/user-properties.service";
import { LoanPropertiesService } from "../user-services/loan-properties.service";
import { StoreService } from "../store.service";
import {
    StepSlug,
    StepStatus,
} from "../../../loan-layout/components/loan-dashboard/components/timeline/timeline.interface";
import { USER_STORE_DATA_ENUM, USER_TYPE_ENUM } from "../user-services/user-types";
import { NetCoreService } from "../netcore/netcore.service";

interface RealTimeEvent {
    data: {
        customerId: number;
        paymentStatus: string;
    };
    message: string;
    status: RealTimeEventStatesEnum;
}

declare let gtag: Function;
declare let dataLayer: any[];

@Injectable({
    providedIn: "root",
})
export class GtmService {
    constructor(
        private router: Router,
        private userPropertiesService: UserPropertiesService,
        private loanPropertiesService: LoanPropertiesService,
        private storeService: StoreService,
        private netCoreService: NetCoreService
    ) {
        if (typeof dataLayer === "undefined") {
            (window as any).dataLayer = [];
        }
    }

    loanProperties = this.loanPropertiesService.getLoanProperties();

    pushToDataLayer(data: any): void {
        const userProperties = this.userPropertiesService.getFormattedUserProperties();

        if (typeof dataLayer !== "undefined") {
            const filteredData = this.userPropertiesService.filterNullValues(data);
            dataLayer.push({ ...filteredData, user: userProperties });
        } else {
            console.error("❌ GTM dataLayer not found!");
        }
    }

    /**
     * Track popup opened event
     */
    trackPopupOpened(popupName: string, parameters: any = {}): void {
        const eventData = {
            event: GTM_EVENTS.PAGE_VIEW_POPUP.pop_up_opened,
            event_detail: parameters.event_detail || popupName,
            platform: PLATFORM_ENUM.web_app,
            page_name: parameters.page_name || "",
            ...(parameters.error_message && {
                error_message: parameters.error_message,
            }),
            ...(parameters.error_type && { error_type: parameters.error_type }),
            ...(parameters.error_url && { error_url: parameters.error_url }),
            ...(parameters.page_location && { page_location: parameters.page_location }),
            ...(parameters.page_referral && { page_referral: parameters.page_referral })
        };

        this.pushToDataLayer(eventData);
    }

    trackPopupClosed(popupName: string, parameters: any = {}): void {
        const eventData = {
            event: GTM_EVENTS.PAGE_VIEW_POPUP.pop_up_closed,
            event_detail: parameters.event_detail || popupName,
            platform: PLATFORM_ENUM.web_app,
            page_name: parameters.page_name || "",
            ...(parameters.error_message && {
                error_message: parameters.error_message,
            }),
            ...(parameters.error_type && { error_type: parameters.error_type }),
            ...(parameters.error_url && { error_url: parameters.error_url }),
        };

        this.pushToDataLayer(eventData);
    }

    trackButtonClick(buttonName: string, section: string = "general"): void {
        this.pushToDataLayer({
            event: "button_click",
            button_name: buttonName,
            section: section,
            timestamp: new Date().toISOString(),
        });
    }

    trackFormSubmission(formName: string, success: boolean = true): void {
        this.pushToDataLayer({
            event: "form_submission",
            form_name: formName,
            success: success,
            timestamp: new Date().toISOString(),
        });
    }

    trackCustomEvent(eventName: string, parameters: any = {}): void {
        const currentUrl = this.router.url;

        const userProperties = this.userPropertiesService.getFormattedUserProperties();

        const standardizedParams = {
            ...parameters,
            ...(parameters.page_name && { page_name: parameters.page_name }),
            platform: PLATFORM_ENUM.web_app,
            error_message: parameters.error_message || null,
            error_type: parameters.error_type || null,
            error_url: parameters.error_message ? parameters.error_url || currentUrl : null,
            timestamp: new Date().toISOString(),
        };

        const eventData = {
            event: eventName,
            ...standardizedParams,
            user: userProperties,
        };

        this.pushToDataLayer(eventData);
    }

    trackEventWithError(eventName: string, parameters: any = {}, error?: any): void {
        const currentUrl = this.router.url;

        const errorParams = {
            ...parameters,
            ...(parameters.page_name && { page_name: parameters.page_name }),
            platform: PLATFORM_ENUM.web_app,
            error_message: error?.message || parameters.error_message || null,
            error_type: error?.type || parameters.error_type || (error ? "api_error" : null),
            error_url: error?.message || parameters.error_message ? parameters.error_url || currentUrl : null,
            timestamp: new Date().toISOString(),
        };

        const eventData = {
            event: eventName,
            ...errorParams,
        };

        this.pushToDataLayer(eventData);
    }

    trackUserLogin(userId: string, loginMethod: string = "email"): void {
        this.pushToDataLayer({
            event: "user_login",
            user_id: userId,
            login_method: loginMethod,
            timestamp: new Date().toISOString(),
        });
    }

    trackUserSignup(userId: string, signupMethod: string = "email"): void {
        this.pushToDataLayer({
            event: "user_signup",
            user_id: userId,
            signup_method: signupMethod,
            timestamp: new Date().toISOString(),
        });
    }

    trackSearch(searchTerm: string, resultsCount: number = 0): void {
        this.pushToDataLayer({
            event: "search",
            search_term: searchTerm,
            results_count: resultsCount,
            timestamp: new Date().toISOString(),
        });
    }

    /**
     * Track page_view event — called automatically on route changes
     */
    trackPageView(pageTitle: string, pageLocation: string, pageReferral?: string): void {
        let userDetails = this.storeService.get("customerDetails");
        this.trackCustomEvent(GTM_EVENT_CONFIG['PAGE_VIEW'].eventName, {
            page_title: pageTitle,
            page_location: pageLocation,
            page_referral: pageReferral || document.referrer || '',
            user_id: userDetails ? JSON.parse(userDetails)?.user_id : null,
        });
    }

    /**
     * Config-driven event tracker — fires any event from GTM_EVENT_CONFIG by key.
     * Usage: this.gtmService.trackConfigEvent('EMPLOYMENT_SUBMIT', { user_id: '123' });
     */
    trackConfigEvent(configKey: string, dynamicParams: Record<string, any> = {}): void {
        const config = GTM_EVENT_CONFIG[configKey];
        if (!config) {
            console.warn(`GTM event config not found: ${configKey}`);
            return;
        }

        let userDetails = this.storeService.get("customerDetails");

        this.trackCustomEvent(config.eventName, {
            platform: PLATFORM_ENUM.web_app,
            application_id: this.storeService.get("applicationId") || null,
            loan_id: this.storeService.get("loanId") || undefined,
            user_id: userDetails ? JSON.parse(userDetails)?.user_id : null,
            ...dynamicParams,
        });
    }

    /**
     * Set user ID in dataLayer for signed-in users
     */
    setUserId(userId: string): void {
        this.userPropertiesService.setUserIdInDataLayer(userId);
    }

    /**
     * Clear user ID from dataLayer for signed-out users
     */
    clearUserId(): void {
        this.userPropertiesService.clearUserIdFromDataLayer();
    }

    /**
     * Refresh user properties
     */
    refreshUserProperties(): void {
        this.userPropertiesService.refreshUserProperties();
    }

    /**
     * Track e-commerce events with proper GA4 Enhanced Ecommerce structure
     */
    trackEcommerceEvent(eventName: string, ecommerceData: any, additionalParams: any = {}): void {
        const userProperties = this.userPropertiesService.getFormattedUserProperties();

        this.pushToDataLayer({ ecommerce: null });

        const currentUrl = this.router.url;

        let pageName = additionalParams.page_name;
        if (!pageName) {
            pageName = this.getPageNameForEcommerceEvent(eventName);
        }

        const eventData = {
            event: eventName,
            ecommerce: ecommerceData,
            ...additionalParams,
            event_detail: additionalParams.event_detail || "",
            page_name: pageName,
            platform: PLATFORM_ENUM.web_app,
            error_message: additionalParams.error_message || null,
            error_type: additionalParams.error_type || null,
            error_url: additionalParams.error_message ? additionalParams.error_url || currentUrl : null,
            timestamp: new Date().toISOString(),
            user: userProperties,
        };

        const { ecommerce, ...otherParams } = eventData;
        const finalEventData = { ...otherParams, ecommerce };

        this.pushToDataLayer(finalEventData);
    }

    private getPageNameForEcommerceEvent(eventName: string): string {
        switch (eventName) {
            case GTM_EVENTS.E_COMMERCE.view_item_list:
            case GTM_EVENTS.E_COMMERCE.select_item:
                return PAGE_NAMES.DASHBOARD;
            case GTM_EVENTS.E_COMMERCE.view_item:
            case GTM_EVENTS.E_COMMERCE.add_to_cart:
                return PAGE_NAMES.LOAN_HOME;
            case GTM_EVENTS.E_COMMERCE.begin_checkout:
                return PAGE_NAMES.FINAL_VERIFICATION;
            case GTM_EVENTS.E_COMMERCE.add_shipping_info:
                return PAGE_NAMES.USER_APPROVAL;
            case GTM_EVENTS.E_COMMERCE.add_payment_info:
                return PAGE_NAMES.E_MANDATE_REGISTRATION;
            case GTM_EVENTS.E_COMMERCE.purchase:
                return PAGE_NAMES.LOAN_DISBURSEMENT;
            default:
                return PAGE_NAMES.DASHBOARD; // Default fallback
        }
    }

    private mapSlugToGA4EcommerceValues(slug: string): {
        item_id: string;
        item_name: string;
        item_category: string;
    } {
        const dashboardProductMapping: {
            [key: string]: {
                item_id: string;
                item_name: string;
                item_category: string;
            };
        } = {
            MAIN_DASHBOARD_LOAN: {
                item_id: "dashboard_loan",
                item_name: "loans",
                item_category: "loans",
            },
            MAIN_DASHBOARD_INVESTMENT: {
                item_id: "dashboard_investment",
                item_name: "investment",
                item_category: "investment",
            },
            MAIN_DASHBOARD_INSURANCE: {
                item_id: "dashboard_insurance",
                item_name: "insurance",
                item_category: "insurance",
            },
            MAIN_DASHBOARD_REFER_EARN: {
                item_id: "dashboard_refer_and_earn",
                item_name: "refer and earn",
                item_category: "refer and earn",
            },
            // Default fallback for loans
            default: {
                item_id: "dashboard_loan",
                item_name: "loans",
                item_category: "loans",
            },
        };

        return dashboardProductMapping[slug] || dashboardProductMapping["default"];
    }

    /**
     * Helper to create e-commerce item structure for loan products (CSV compliant)
     */
    createLoanProductItem(itemData: {
        item_id?: string;
        item_name?: string;
        item_category?: string;
        price?: number;
        quantity?: number;
        index?: number;
        discount?: number;
        coupon?: string;
        interest_rate?: number;
        loan_tenure?: number;
        processing_fee?: number;
        slug?: string;
        [key: string]: any;
    }): any {
        // Use GA4 E-commerce mapping if slug is provided, otherwise use provided values
        let ecommerceItemData = {
            item_id: itemData.item_id || "",
            item_name: itemData.item_name || "",
            item_category: itemData.item_category || "",
        };
        if (itemData.slug) {
            ecommerceItemData = this.mapSlugToGA4EcommerceValues(itemData.slug);
        }

        return {
            item_id: ecommerceItemData.item_id,
            item_name: ecommerceItemData.item_name,
            item_category: ecommerceItemData.item_category,
            price: itemData.price || 0,
            ...(itemData.quantity && { quantity: itemData.quantity }),
            index: itemData.index || 0,
            ...(itemData.discount && { discount: itemData.discount }),
            ...(itemData.coupon && { coupon: itemData.coupon }),
            ...(itemData.interest_rate && { interest_rate: itemData.interest_rate }),
            ...(itemData.loan_tenure && { loan_tenure: itemData.loan_tenure }),
            ...(itemData.processing_fee && {
                processing_fee: itemData.processing_fee,
            }),
            item_list_id: "all_products",
            item_list_name: "dashboard",
        };
    }

    initTimelineStates = {
        [TimelineEventStatesEnum.EMPLOYMENT_VERIFICATION_SUBMITTED]: eventStates,
        [TimelineEventStatesEnum.EMPLOYMENT_VERIFICATION_FAILED]: eventStates,
        [TimelineEventStatesEnum.BANK_VERIFICATION_FAILED]: eventStates,
        [TimelineEventStatesEnum.BANK_VERIFICATION_SUCCESS]: eventStates,
        [TimelineEventStatesEnum.CKYC_VERIFICATION_FAIL]: eventStates,
        [TimelineEventStatesEnum.CKYC_VERIFICATION_SUCCESS]: eventStates,
        [TimelineEventStatesEnum.LOAN_RESULT_APPROVED]: eventStates,
        [TimelineEventStatesEnum.LOAN_RESULT_REJECTED]: eventStates,
        [TimelineEventStatesEnum.E_MANDATE_SUCCESS]: eventStates,
        [TimelineEventStatesEnum.E_MANDATE_FAILED]: eventStates,
        [TimelineEventStatesEnum.E_SIGN_DOCUMENT_SUCCESS]: eventStates,
        [TimelineEventStatesEnum.E_SIGN_DOCUMENT_FAILED]: eventStates,
        [TimelineEventStatesEnum.VIDEO_VERIFICATION_SUCCESS]: eventStates,
        [TimelineEventStatesEnum.VIDEO_VERIFICATION_FAILED]: eventStates,
        [TimelineEventStatesEnum.LOAN_DISBURSED]: eventStates,
        [TimelineEventStatesEnum.EMPLOYMENT_VERIFICATION_BEGINS]: eventStates,
        [TimelineEventStatesEnum.ADD_TO_CART]: eventStates,
        [TimelineEventStatesEnum.FINAL_VERIFICATION_BEGINS]: eventStates,
    };
    // Employment verification begins event it only fires once per session (per application)
    handleEmploymentVerificationBeginsEvent(): void {
        const applicationId = this.loanProperties.applicationId?.toString() || "";
        const eventStates = this.getTimelineEventStates(applicationId);

        // Check if event already fired for this application
        if (eventStates[TimelineEventStatesEnum.EMPLOYMENT_VERIFICATION_BEGINS]?.isFired) return;

        eventStates[TimelineEventStatesEnum.EMPLOYMENT_VERIFICATION_BEGINS] = { isFired: true, status: true };
        this.saveTimelineEventStates(eventStates, applicationId);
    }

    // Face verification events - fire only once per application
    handleFaceVerificationEvent(status: "success" | "failed"): void {
        this.trackCustomEvent(GTM_EVENTS.DOCUMENT_VERIFICATION.face_verification(status), {
            platform: PLATFORM_ENUM.web_app,
            page_name: PAGE_NAMES.VERIFICATION,
        });
    }

    // Add to cart event - fire only once per application
    handleAddToCartEvent(): void {
        const applicationId = this.loanProperties.applicationId?.toString() || "";
        const eventStates = this.getTimelineEventStates(applicationId);

        // Check if event already fired for this application
        if (eventStates[TimelineEventStatesEnum.ADD_TO_CART]?.isFired) return;

        const employmentItem = this.createLoanProductItem({
            item_id: "dashboard_loan",
            item_name: "loans",
            item_category: "loans",
            quantity: 1,
        });

        this.trackEcommerceEvent(GTM_EVENTS.E_COMMERCE.add_to_cart, {
            currency: "INR",
            items: [employmentItem],
        });

        eventStates[TimelineEventStatesEnum.ADD_TO_CART] = { isFired: true, status: true };
        this.saveTimelineEventStates(eventStates, applicationId);
    }

    // Final verification begins event - fire only once per application when all required steps are completed
    handleFinalVerificationBeginsEvent(response: any): void {
        if (!response?.success || !response?.data?.timeLine) return;

        const applicationId = response.data.loanApplicationId?.toString() || "";
        const eventStates = this.getTimelineEventStates(applicationId);

        // Check if event already fired for this application
        if (eventStates[TimelineEventStatesEnum.FINAL_VERIFICATION_BEGINS]?.isFired) return;
        const requiredSteps: StepSlug[] = [StepSlug.EMP_VERIFI_STEP, StepSlug.NB_BS_AG_ETC];

        // Check if all required steps have status COMPLETED
        const stepStatuses: { [key: string]: string } = {};

        response.data.timeLine.forEach((group: any) => {
            group.steps?.forEach((step: any) => {
                if (step.stepSlug && requiredSteps.includes(step.stepSlug)) {
                    stepStatuses[step.stepSlug] = step.statusCode;
                }
            });
        });

        // Check if all required steps are completed
        const allStepsCompleted = requiredSteps.every((stepSlug) => stepStatuses[stepSlug] === StepStatus.COMPLETED);

        if (allStepsCompleted) {
            console.log("allStepsCompleted", allStepsCompleted, "firing final verification");

            this.trackCustomEvent(GTM_EVENTS.CKYC.final_verification_begins, {
                platform: PLATFORM_ENUM.web_app,
                page_name: PAGE_NAMES.FINAL_VERIFICATION,
                application_id: applicationId,
                loan_status: "final_verification_begins",
            });
            eventStates[TimelineEventStatesEnum.FINAL_VERIFICATION_BEGINS] = { isFired: true, status: true };
            this.saveTimelineEventStates(eventStates, applicationId);
        }
    }

    private getTimelineEventStates(applicationId: string): typeof this.initTimelineStates {
        const stored = this.storeService.get(USER_STORE_DATA_ENUM.gtmTimelineEvents(applicationId));
        return (stored ? JSON.parse(stored) : this.initTimelineStates) as typeof this.initTimelineStates;
    }

    private saveTimelineEventStates(eventStates: typeof this.initTimelineStates, applicationId: string): void {
        this.storeService.set({
            key: USER_STORE_DATA_ENUM.gtmTimelineEvents(applicationId),
            value: JSON.stringify(eventStates),
        });
    }

    clearTimelineEventStates(): void {
        const applicationId = this.loanProperties.applicationId?.toString() || "";
        this.storeService.remove(USER_STORE_DATA_ENUM.gtmTimelineEvents(applicationId));
    }

    private fireTimelineEvent(eventName: string, parameters: any): void {
        this.trackCustomEvent(eventName, {
            platform: PLATFORM_ENUM.web_app,
            ...parameters,
        });
    }

    // Gets data from Timeline API and fires the events
    processTimelineResponse(response: any): void {
        if (response?.success && response?.data?.timeLine) {
            const applicationId = response.data.loanApplicationId?.toString() || "";
            response.data.timeLine.forEach((group: any) => {
                group.steps?.forEach((step: any) => {
                    if (step.stepSlug && step.statusCode) {
                        this.trackTimelineStepEvent(step.stepSlug, step.statusCode, applicationId, step);
                    }
                });
            });

            // Check if final verification should begin
            this.handleFinalVerificationBeginsEvent(response);
        }
    }

    // To process MQTT response and fire the events
    processRealTimeEvents(event: RealTimeEvent): void {
        const loanProperties = this.loanPropertiesService.getLoanProperties();
        switch (event.status) {
            case RealTimeEventStatesEnum.PAYMENT_SUCCESS_FAILED:
                if (event.data.paymentStatus !== "Success") {
                    this.handleRepaymentFailedEvent(event.status, event.message);
                }
                break;
            case RealTimeEventStatesEnum.FINAL_VERIFICATION_VERIFIED:
                // Fire loan rejected event
                if (event.message === "DECLINED") {
                    const eventStates = this.getTimelineEventStates(loanProperties?.applicationId?.toString() || "");
                    this.handleLoanStatusEvent(
                        eventStates,
                        StepStatus.DECLINED,
                        loanProperties?.applicationId?.toString() || "",
                        event.message
                    );
                } else if (event.message === "APPROVED") {
                    const eventStates = this.getTimelineEventStates(loanProperties?.applicationId?.toString() || "");
                    this.handleLoanStatusEvent(
                        eventStates,
                        StepStatus.COMPLETED,
                        loanProperties?.applicationId?.toString() || ""
                    );
                }
                break;
        }
    }

    private handleRepaymentFailedEvent(status: RealTimeEventStatesEnum, message?: string): void {
        const loanProperties = this.loanPropertiesService.getLoanProperties();
        this.trackEventWithError(GTM_EVENTS.REPAYMENT.repayment_failed, {
            platform: PLATFORM_ENUM.web_app,
            status: status,
            error: "Payment failed",
            application_id: loanProperties?.applicationId?.toString() || "",
            loan_id: loanProperties?.loanId?.toString() || "",
            error_url: window.location.href,
            error_type: "razorpay_error",
            error_message: message || "Payment failed",
        });
    }

    private trackTimelineStepEvent(stepSlug: string, statusCode: string, applicationId: string, step?: any): void {
        const eventStates = this.getTimelineEventStates(applicationId);

        switch (stepSlug) {
            case StepSlug.EMP_VERIFI_STEP:
                this.handleEmploymentEvent(eventStates, statusCode, applicationId, step);
                break;
            case StepSlug.NB_BS_AG_ETC:
                this.handleBankDetailsEvent(eventStates, statusCode, applicationId);
                break;
            case StepSlug.CKYC_VERIF_STEP:
                this.handleCkycEvent(eventStates, statusCode, applicationId);
                break;
            case StepSlug.FINAL_VERIFICATION:
                this.handleLoanStatusEvent(eventStates, statusCode, applicationId);
                break;
            case StepSlug.VIDEO_KYC:
                this.handleVideoVerificationEvent(eventStates, statusCode, applicationId);
                break;
            case StepSlug.ESIGN_STEP:
                this.handleESignEvent(eventStates, statusCode, applicationId);
                break;
            case StepSlug.LOAN_DISBURSEMENT:
                this.handleLoanDisbursementEvent(eventStates, statusCode, applicationId);
                break;
            case StepSlug.ENACH_STEP:
                this.handleEMandateEvent(eventStates, statusCode, applicationId);
                break;
        }
    }

    private handleEmploymentEvent(
        eventStates: typeof this.initTimelineStates,
        statusCode: string,
        applicationId: string,
        step?: any
    ): void {
        const currentCompany = step?.data?.currentCompany || {};
        const userProperties = this.userPropertiesService.getFormattedUserProperties();
        const employedType =
            this.storeService.get("employedType")?.toString() ||
            this.storeService.get("employmentStatus")?.toString() ||
            "";

        const parameters = {
            page_name: PAGE_NAMES.LOAN_ELIGIBILITY_EMPLOY_VERIFICATION,
            application_id: applicationId,
            company_name: currentCompany.companyName || userProperties.company_name || "",
            company_type: currentCompany.companyType || userProperties.company_type || "",
            company_size: currentCompany.companySize || "",
            self_employed_status: employedType === "2" ? "Y" : "N",
            user_id: userProperties.user_id || "",
        };

        const employmentVerificationSuccess = TimelineEventStatesEnum.EMPLOYMENT_VERIFICATION_SUBMITTED;
        const employmentVerificationFailed = TimelineEventStatesEnum.EMPLOYMENT_VERIFICATION_FAILED;

        switch (statusCode) {
            case StepStatus.FAILED:
            case StepStatus.DECLINED:
                if (eventStates[employmentVerificationFailed].isFired) return;
                eventStates[employmentVerificationFailed] = { isFired: true, status: true };
                this.fireTimelineEvent(GTM_EVENTS.EMPLOYMENT_VERIFICATION.employment_verification_failed, {
                    ...parameters,
                    loan_status: "company_details_verified_fail",
                    error_message: "Verification failed",
                    error_type: "api_error",
                    error_url: window.location.href,
                });
                break;
            case StepStatus.COMPLETED:
                if (eventStates[employmentVerificationSuccess].isFired) return;
                eventStates[employmentVerificationSuccess] = { isFired: true, status: true };
                this.fireTimelineEvent(
                    GTM_EVENTS.EMPLOYMENT_VERIFICATION.employment_verification_submitted,
                    {
                        ...parameters,
                        loan_status: "company_details_verified_success",
                    }
                );
                break;
        }
        this.saveTimelineEventStates(eventStates, applicationId);
    }

    private handleBankDetailsEvent(
        eventStates: typeof this.initTimelineStates,
        statusCode: string,
        applicationId: string
    ): void {
        const bankVerificationSuccess = TimelineEventStatesEnum.BANK_VERIFICATION_SUCCESS;
        const parameters = {
            page_name: PAGE_NAMES.BANK_VERIFICATION,
            application_id: applicationId,
        };

        switch (statusCode) {
            case StepStatus.DECLINED:
            case StepStatus.FAILED:
                this.fireTimelineEvent(GTM_EVENTS.BANK_VERIFICATION.bank_verification_result("failed"), {
                    ...parameters,
                    loan_status: "bank_verification_failed",
                    error_message: "Verification failed",
                    error_type: "api_error",
                    error_url: window.location.href,
                });
                break;
            case StepStatus.COMPLETED:
                // if (eventStates[bankVerificationSuccess].isFired) return;
                // eventStates[bankVerificationSuccess] = { isFired: true, status: true };
                // this.fireTimelineEvent(GTM_EVENTS.BANK_VERIFICATION.bank_verification_result("success"), {
                //     ...parameters,
                //     loan_status: "bank_verification_success",
                // });
                break;
        }
        this.saveTimelineEventStates(eventStates, applicationId);
    }

    private handleCkycEvent(
        eventStates: typeof this.initTimelineStates,
        statusCode: string,
        applicationId: string
    ): void {
        const ckycVerificationSuccess = TimelineEventStatesEnum.CKYC_VERIFICATION_SUCCESS;

        const parameters = {
            page_name: PAGE_NAMES.CKYC_VERIFICATION,
            application_id: applicationId,
        };

        switch (statusCode) {
            case StepStatus.FAILED:
            case StepStatus.DECLINED:
                this.fireTimelineEvent(GTM_EVENTS.CKYC.ckyc_verification("fail"), {
                    ...parameters,
                    loan_status: "ckyc_verification_fail",
                    error_message: "Verification failed",
                    error_type: "api_error",
                    error_url: window.location.href,
                });
                break;
            case StepStatus.COMPLETED:
                if (eventStates[ckycVerificationSuccess].isFired) return;
                eventStates[ckycVerificationSuccess] = { isFired: true, status: true };
                this.fireTimelineEvent(GTM_EVENTS.CKYC.ckyc_verification("success"), {
                    ...parameters,
                    loan_status: "ckyc_verification_success",
                });
                break;
        }
        this.saveTimelineEventStates(eventStates, applicationId);
    }

    private handleLoanStatusEvent(
        eventStates: typeof this.initTimelineStates,
        statusCode: string,
        applicationId: string,
        loanRejectionReason?: string
    ): void {
        const loanRejected = TimelineEventStatesEnum.LOAN_RESULT_REJECTED;
        const loanApproved = TimelineEventStatesEnum.LOAN_RESULT_APPROVED;

        const parameters = {
            page_name: PAGE_NAMES.CKYC_VERIFICATION,
            application_id: applicationId,
            loan_id: this.loanProperties.loanId,
        };

        switch (statusCode) {
            case StepStatus.DECLINED:
            case StepStatus.FAILED:
                this.fireTimelineEvent(loanRejected, {
                    ...parameters,
                    loan_status: "loan_rejected",
                    error_message: "Verification failed",
                    error_type: "api_error",
                    error_url: window.location.href,
                    loan_rejection_reason: loanRejectionReason || "Final verirfication failed",
                });
                break;
            case StepStatus.COMPLETED:
                if (eventStates[loanApproved].isFired) return;
                eventStates[loanApproved] = { isFired: true, status: true };
                this.fireTimelineEvent(loanApproved, {
                    ...parameters,
                    loan_status: "loan_approved",
                });
                break;
        }
        this.saveTimelineEventStates(eventStates, applicationId);
    }

    private handleVideoVerificationEvent(
        eventStates: typeof this.initTimelineStates,
        statusCode: string,
        applicationId: string
    ): void {
        const videoVerificationFailed = TimelineEventStatesEnum.VIDEO_VERIFICATION_FAILED;
        const videoVerificationSuccess = TimelineEventStatesEnum.VIDEO_VERIFICATION_SUCCESS;

        const parameters = {
            page_name: PAGE_NAMES.VIDEO_VERIFICATION,
            application_id: applicationId,
            loan_id: this.loanProperties.loanId,
        };

        switch (statusCode) {
            case StepStatus.FAILED:
            case StepStatus.DECLINED:
                this.fireTimelineEvent(videoVerificationFailed, {
                    ...parameters,
                    loan_status: "video_verification_failed",
                    error_message: "Verification failed",
                    error_type: "api_error",
                    error_url: window.location.href,
                });
                break;
            case StepStatus.COMPLETED:
                if (eventStates[videoVerificationSuccess].isFired) return;
                eventStates[videoVerificationSuccess] = { isFired: true, status: true };
                this.fireTimelineEvent(videoVerificationSuccess, {
                    ...parameters,
                    loan_status: "video_verification_success",
                });
                break;
        }
        this.saveTimelineEventStates(eventStates, applicationId);
    }

    private handleESignEvent(
        eventStates: typeof this.initTimelineStates,
        statusCode: string,
        applicationId: string
    ): void {
        const eSignFailed = TimelineEventStatesEnum.E_SIGN_DOCUMENT_FAILED;
        const eSignVerified = TimelineEventStatesEnum.E_SIGN_DOCUMENT_SUCCESS;

        const parameters = {
            page_name: PAGE_NAMES.E_SIGN_VERIFICATION,
            application_id: applicationId,
            loan_id: this.loanProperties.loanId,
        };

        switch (statusCode) {
            case StepStatus.FAILED:
            case StepStatus.DECLINED:
                this.fireTimelineEvent(eSignFailed, {
                    ...parameters,
                    loan_status: "e_sign_document_failed",
                    error_message: "Verification failed",
                    error_type: "api_error",
                    error_url: window.location.href,
                });
                break;
            case StepStatus.COMPLETED:
                if (eventStates[eSignVerified].isFired) return;
                eventStates[eSignVerified] = { isFired: true, status: true };
                this.fireTimelineEvent(eSignVerified, {
                    ...parameters,
                    loan_status: "e_sign_document_success",
                });
                break;
        }
        this.saveTimelineEventStates(eventStates, applicationId);
    }

    private handleEMandateEvent(
        eventStates: typeof this.initTimelineStates,
        statusCode: string,
        applicationId: string
    ): void {
        const eMandateFailed = TimelineEventStatesEnum.E_MANDATE_FAILED;
        const eMandateVerified = TimelineEventStatesEnum.E_MANDATE_SUCCESS;

        const parameters = {
            page_name: PAGE_NAMES.E_MANDATE_REGISTRATION,
            application_id: applicationId,
            loan_id: this.loanProperties.loanId,
        };

        switch (statusCode) {
            case StepStatus.FAILED:
            case StepStatus.DECLINED:
                this.fireTimelineEvent(eMandateFailed, {
                    ...parameters,
                    loan_status: "emandate_failed",
                    error_message: "Verification failed",
                    error_type: "api_error",
                    error_url: window.location.href,
                });
                break;
            case StepStatus.COMPLETED:
                if (eventStates[eMandateVerified].isFired) return;
                eventStates[eMandateVerified] = { isFired: true, status: true };
                this.fireTimelineEvent(eMandateVerified, {
                    ...parameters,
                    loan_status: "emandate_success",
                });
                break;
        }
        this.saveTimelineEventStates(eventStates, applicationId);
    }

    private handleLoanDisbursementEvent(
        eventStates: typeof this.initTimelineStates,
        statusCode: string,
        applicationId: string
    ): void {
        const loanDisbursementSuccess = TimelineEventStatesEnum.LOAN_DISBURSED;

        const parameters = {
            page_name: PAGE_NAMES.E_MANDATE_REGISTRATION,
            application_id: applicationId,
            loan_id: this.loanProperties.loanId,
        };

        switch (statusCode) {
            case StepStatus.COMPLETED:
                // moved to s2s
                // this.fireTimelineEvent(loanDisbursementSuccess, {
                //     ...parameters,
                //     loan_status: "loan_disbursed",
                // });
                break;
        }
        this.saveTimelineEventStates(eventStates, applicationId);
    }

    // Handle employment details submitted event with Netcore tracking
    handleEmploymentDetailsSubmitted(): void {
        const applicationId = this.loanProperties.applicationId?.toString() || "";

        // Track GTM event

        // Track company details in Netcore
        const userProperties = this.userPropertiesService.getFormattedUserProperties();
        this.netCoreService.trackUserProperties({
            COMPANY_NAME: userProperties.company_name || "",
            COMPANY_TYPE: userProperties.company_type || "",
            USER_SALARY: userProperties.user_salary,
        });
    }
}
